import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import {
  CreateHiddenPageRequest,
  IHiddenPage,
  UpdateHiddenPageRequest,
} from "../models/HiddenPages";
import { DefaultResponseWithData } from "../models/system";

// If you already have a standard response wrapper type, swap this to it.
export interface IHiddenPageResponse {
  success: boolean;
  message: string;
  data: IHiddenPage;
}

export default function useHiddenPageSingle({ hiddenPageId }: { hiddenPageId?: string }) {
  const { get, put, post, del } = useApiHelper();
  const queryClient = useQueryClient();

  const hiddenPage = useQuery({
    queryKey: ["hiddenPages", hiddenPageId],
    queryFn: () => get<IHiddenPageResponse>(`/hiddenpages/${hiddenPageId}`),
    refetchInterval: 1000 * 10,
    enabled: !!hiddenPageId,
  });

  const create = useMutation(
    (req: CreateHiddenPageRequest) => post(`/hiddenpages`, req),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["hiddenPages"]);
      },
    }
  );

  const update = useMutation(
    (payload: { id: string; req: UpdateHiddenPageRequest }) =>
      put(`/hiddenpages/${payload.id}`, payload.req),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["hiddenPages"]);
        // optionally:
        // queryClient.invalidateQueries(["hiddenPages", payload.id]); // can't access payload here easily
      },
    }
  );

  const deleteSetting = useMutation(
    (id: string) => del(`/hiddenpages/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["hiddenPages"]);
      },
    }
  );

  return {
    hiddenPage,
    create,
    update,
    deleteSetting,
  };
}
